class ExposureHighlightsCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.track?.querySelectorAll('[data-slide]') || []);
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.dotsContainer = this.querySelector('[data-carousel-dots]');
    this.breakpoint = Number(this.dataset.mobileBreakpoint || 749);
    this.index = 0;
    this.touchStartX = 0;

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.onResize = this.onResize.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.onResize();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('touchstart', this.onTouchStart);
    this.viewport?.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('resize', this.onResize);
  }

  isCarouselMode() {
    if (!this.classList.contains('exposure-highlights__carousel--enabled')) return false;
    return window.matchMedia(`(max-width: ${this.breakpoint}px)`).matches;
  }

  onResize() {
    if (!this.isCarouselMode()) {
      this.track.style.transform = '';
      this.classList.remove('is-carousel-active');
      if (this.dotsContainer) this.dotsContainer.innerHTML = '';
      return;
    }

    this.classList.add('is-carousel-active');
    if (this.index >= this.slides.length) this.index = 0;
    this.buildDots();
    this.update();
  }

  buildDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    this.dots = [];

    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'exposure-highlights__dot';
      dot.setAttribute('aria-label', `Show highlight ${i + 1}`);
      dot.addEventListener('click', () => {
        this.index = i;
        this.update();
      });
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    });
  }

  onPrev() {
    if (!this.isCarouselMode()) return;
    this.index = this.index <= 0 ? this.slides.length - 1 : this.index - 1;
    this.update();
  }

  onNext() {
    if (!this.isCarouselMode()) return;
    this.index = this.index >= this.slides.length - 1 ? 0 : this.index + 1;
    this.update();
  }

  onTouchStart(event) {
    if (!this.isCarouselMode()) return;
    this.touchStartX = event.changedTouches?.[0]?.clientX ?? 0;
  }

  onTouchEnd(event) {
    if (!this.isCarouselMode()) return;
    const touchEndX = event.changedTouches?.[0]?.clientX ?? 0;
    const delta = touchEndX - this.touchStartX;

    if (Math.abs(delta) < 40) return;
    if (delta < 0) this.onNext();
    else this.onPrev();
  }

  update() {
    if (!this.isCarouselMode()) return;

    const offset = this.index * this.viewport.clientWidth;
    this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;

    this.prevBtn?.toggleAttribute('disabled', this.slides.length <= 1);
    this.nextBtn?.toggleAttribute('disabled', this.slides.length <= 1);

    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('exposure-highlights-carousel')) {
  customElements.define('exposure-highlights-carousel', ExposureHighlightsCarousel);
}
